import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Copy, Trash2, Film, Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";

export default function MediaLibrary() {
  const [items, setItems] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/media").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load media"));
  }, []);
  useEffect(load, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/media", fd);
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error("Upload failed — max 10MB");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.filename}? Any section using it will lose the image.`)) return;
    await api.delete(`/admin/media/${m.id}`).catch(() => toast.error("Delete failed"));
    toast.success("Deleted");
    load();
  };

  const copy = (m) => {
    navigator.clipboard?.writeText(mediaUrl(m.url));
    toast.success("URL copied to clipboard");
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight">Media Library</h1>
          <p className="text-xs text-subtle mt-1.5">Upload images & videos, copy their URLs into any content field.</p>
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent-bright disabled:opacity-60 text-white font-semibold px-5 py-2.5 text-xs transition-all"
          data-testid="admin-media-upload-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Upload File"}
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="admin-media-file-input" />
      </div>

      {!items ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 text-accent-bright animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-sm text-subtle" data-testid="admin-media-empty">No media uploaded yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m, i) => (
            <div key={m.id} className="glass rounded-2xl overflow-hidden group" data-testid={`admin-media-card-${i}`}>
              <div className="aspect-video bg-[#131327] flex items-center justify-center overflow-hidden">
                {m.content_type?.startsWith("image/") ? (
                  <img src={mediaUrl(m.url)} alt={m.filename} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <Film className="h-8 w-8 text-subtle" />
                )}
              </div>
              <div className="p-3">
                <p className="text-xs font-semibold text-white truncate mb-2">{m.filename}</p>
                <div className="flex gap-1.5">
                  <button onClick={() => copy(m)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] font-semibold px-2 py-1.5 rounded-lg border border-white/15 text-mist hover:text-white hover:border-accent/50 transition-colors" data-testid={`admin-media-copy-${i}`}>
                    <Copy className="h-3 w-3" /> Copy URL
                  </button>
                  <button onClick={() => remove(m)} className="p-1.5 rounded-lg text-subtle hover:text-rose-400 hover:bg-rose-500/10 transition-colors" data-testid={`admin-media-delete-${i}`} aria-label="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
